import { versionedDay } from '@/lib/versioned-labels';
import type { NpcReferents } from '@matter_extractor/shared';
import { Link } from '@tanstack/react-router';

/**
 * « Dans les bancs » (npc.md §12; maquette `Fiche`, planche 1): the benches that CARRY this NPC.
 *
 * It takes the place of « Utilisé dans » in the right column - an NPC is cited by nothing (§3.4),
 * and a bench is not versioned (H4), so what an operator asks of the aside is « où est-il joué, en
 * série » and the answer is a list of benches. Each name opens the bench in the Bancs tab; a retired
 * bench stays listed with its date, because it still carries the NPC and is restored with it.
 *
 * `referents` is `undefined` while the read is in flight and `null` when it failed: the panel says
 * which, and never says « aucun banc » over a list it did not read.
 */

export interface NpcBenchesPanelProps {
  referents: NpcReferents | null | undefined;
}

export function NpcBenchesPanel({ referents }: NpcBenchesPanelProps) {
  return (
    <section
      data-testid="npc-dans-les-bancs"
      className="space-y-2 rounded-md border-2 border-gray-200 bg-white p-5"
    >
      <h2 className="font-semibold text-bleu-nuit text-body-lg leading-7">Dans les bancs</h2>
      {referents === undefined ? (
        <p className="text-gray-500 text-small italic">Lecture des bancs…</p>
      ) : referents === null ? (
        <p className="text-gray-600 text-small">Les bancs n’ont pas pu être lus.</p>
      ) : referents.benches.length === 0 ? (
        <p data-testid="npc-dans-les-bancs-vide" className="text-gray-600 text-small">
          Aucun banc ne le porte.
        </p>
      ) : (
        <ul className="space-y-1">
          {referents.benches.map((bench) => (
            <li key={bench.id} data-testid="npc-banc-referent" className="text-small">
              <Link
                to="/admin/npc"
                search={{ onglet: 'bancs', banc: bench.id }}
                className="text-bleu-nuit underline underline-offset-2"
              >
                {bench.name}
              </Link>
              {bench.retiredAt === null ? null : (
                <span className="text-gray-600"> · retiré le {versionedDay(bench.retiredAt)}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
